/**
 * @author HungNS
 */
import Vue from "vue";
import VueRouter from "vue-router";
import Index from "@/views/index/Index.vue";
import Dashboard from "@/views/index/dashboard/Dashboard.vue";
import Deposit from "@/views/index/deposit/Deposit.vue";
import Report from "@/views/index/report/Report.vue";
import CreateVirtualBank from "@/views/index/deposit/CreateVirtualBank.vue";
import Transfer from "@/views/index/transfer/Transfer.vue";
import Withdrawal from "@/views/index/withdrawal/Withdrawal.vue";
import BankAccount from "@/views/index/withdrawal/BankAccount.vue";
import NewsList from "@/views/index/news/NewsList.vue";
import AgreementNews from "@/views/agreement-news/Index.vue";
import ForceNews from "@/views/force-news/Index";
import EditProfile from "@/views/index/edit-profile/EditProfile";
import ConfirmMail from "@/views/confirm-mail/ConfirmMail.vue";
import Login from "@/views/login/Login.vue";
import CustomerSupport from "@/views/index/customer-support/CustomerSupport"
import History from "@/views/index/history/History";
import RegisterPasskey from "@/views/passkey/RegisterPasskey";
import IConstants from '@/common/constants/IConstants';
import store from '@/store/index';
import Config from "@/services/Config";
import 'isomorphic-fetch'
import * as HEADER_TYPE from "@/store/modules/header/type";
import * as NEWS_TYPE from "@/store/modules/news/type";
import ServiceUtils from "@/common/utils/ServiceUtils";
import EventBus from "@/services/EventBus";
/* eslint-disable no-unused-vars */

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
    return originalPush.call(this, location).catch(err => err)
};

Vue.use(VueRouter);

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes: [
        {
            path: "/index",
            name: "index",
            redirect: "/index/home",
            component: Index,
            children: [
                {
                    path: "home",
                    name: "home",
                    component: Dashboard,
                },
                {
                    path: "news-list",
                    name: "newsList",
                    component: NewsList,
                },
                {
                    path: "deposit",
                    name: "deposit",
                    component: Deposit,
                },
                {
                    path: "create-virtual-bank",
                    name: "createVirtualBank",
                    component: CreateVirtualBank
                },
                {
                    path: "withdrawal",
                    name: "withdrawal",
                    component: Withdrawal,
                },
                {
                    path: 'bank-account',
                    name: 'bankAccount',
                    component: BankAccount
                },
                {
                    path: "transfer",
                    name: "transfer",
                    component: Transfer,
                },
                {
                    path: "report",
                    name: "report",
                    component: Report,
                },
                {
                    path: "edit-profile",
                    name: "editProfile",
                    component: EditProfile,
                },
                {
                    path: "customer-support",
                    name: "customerSupport",
                    component: CustomerSupport,
                },
                {
                    path: "history",
                    name: "history",
                    component: History,
                },

            ],
        },
        {
            path: "/login",
            name: "login",
            component: Login
        },
        {
            path: "/agree",
            name: "agreeNews",
            component: AgreementNews,
        },
        {
            path: "/force-news",
            name: "forceNews",
            component: ForceNews,
        },
        {
            path: "/profile/confirm-mail",
            name: "confirmMail",
            component: ConfirmMail,
        },
        {
            path: "/account/register-passkey",
            name: "registerPasskey",
            component: RegisterPasskey,
        },
        {
            path: "*", redirect: "/index"
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        return {x: 0, y: 0, behavior: "smooth"};
    },
})

const domain = Config.getDomain();
router.beforeEach(async (to, from, next) => {
    const {name: routerName} = to;

    if (routerName === "confirmMail") return next();

    // this logic for local only, for non-local environment, the uri will always be ${window.location.origin}/mypage/login ( at least for current UAT setting - 3/31/2022 )
    //routerName == 'login' in UAT means mypage/login -> considered arbitrary url, in local, it means redirection to dev login form - /login
    if (routerName === 'login') {
        if (!Config.isUatEnvironment()) return next();
        else return next("/index")
    }

    // Purpose of this logic block: handle case CASTGC ticket expires while session client stills active
    if(!store.state.header.isAuthenticated) {
        try {
            if (ServiceUtils.detectUnsupportedBrowser()) {
                ServiceUtils.showUnsupportedBrowserModal();
                return next(false);
            }
            // "/rest/index" is a dummy API which requires authenticated user. By calling it here, we force call CAS flow in case CASTGC ticket expired
            // why use fetch instead of axios here? because axios cannot handle response 302 (which body is form login) from CAS, so we use fetch to detect and redirect manually
            const response = await fetch(domain + "/rest/index" );
            if (ServiceUtils.validateServerErrorHttpStatus(response.status)) {
                return next(false);
            }
            response.text().then(function(text) {
                const data = ServiceUtils.parseStringJSON(text)
                if (ServiceUtils.checkMaintainModeEOW(data)) {
                    return next(false);
                }
            })
            if (response.redirected && response.url && response.url.lastIndexOf("login") >= 0) {
                window.location.href = Config.getRootDomain() + `/login`;
                return next(false);
            }
            await store.dispatch('header/' + HEADER_TYPE.SET_IS_AUTHENTICATED, true);
        } catch (ex) {
            ServiceUtils.showServerErrorModal();
            return next(false);
        }
    }
    if (routerName !== 'agreeNews' && routerName !== 'forceNews' && routerName !== 'registerPasskey'){
        //has session
        //init active menu items
        let activeMenuItem = null;
        const leftMenu = [...IConstants.leftMenuItems];
        leftMenu.forEach(({link}) => {
            if (link.name === routerName) {
                activeMenuItem =routerName;
            }
        });
        if (activeMenuItem) {
            store.commit('leftMenu/changeActiveMenuItem', activeMenuItem);
        } else {
            store.commit('leftMenu/changeActiveMenuItem', '');
        }

        //bootstrap headers info and news
        if(!store.state.header.isHeaderInfoInit){
            await store.dispatch('header/'+HEADER_TYPE.GET_HEADER_INFO);
        }

        if(store.state.news.isNewsInit) EventBus.$emit('newsFetched', true);
        else {
            await store.dispatch('news/'+NEWS_TYPE.INIT_AGREE_NEWS);
            EventBus.$emit('newsFetched', false);
        }

        // prioritization of screens that users must complete first
        // register passkey > accept re-agreement news > accept force news > accept agreement news(show popup at home screen)
        if (store.state['news'].needRegisterPasskey) {
            return next({path: '/account/register-passkey'});
        } else if (store.state['news'].reAgreementNewsFlg) {
            return next({path: '/agree'});
        } else if (store.state['news'].forceNewsFlg) {
            return  next({path: '/force-news'});
        } else if (store.state['news'].agreeNewsInfoFlg && routerName !== 'home'){
            return next({path: '/index'})
        } else return next();
    } else {
        // handle cases where users manually access a URL that is not allowed by business logic
        if((routerName === 'registerPasskey' && !store.state['news'].needRegisterPasskey) ||
            (routerName === 'agreeNews' && !store.state['news'].reAgreementNewsFlg ) ||
            (routerName === 'forceNews' && !store.state['news'].forceNewsFlg)) {
            return next({path: 'index'});
        }
        return next();
    }
});

export default router;
